// src/pages/PublicProfiles.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { formatDate } from "../utils/dateUtils";
import GenderBadge from "../components/GenderBadge";
import ProfileDetailsModal from "../components/ProfileDetailsModal";

const createInitialFilters = () => ({
  search: "",
  gender: "",
  maritalStatus: "",
  city: "",
  minAge: "",
  maxAge: "",
  profession: "",
  cast: "",
  education: "",
  houseStatus: "",
  bureauId: "",
});

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const stringValue = typeof value === "string" ? value.trim() : `${value}`;
    if (stringValue !== "") searchParams.append(key, stringValue);
  });
  return searchParams.toString();
};
export default function PublicProfiles() {
  const [users, setUsers] = useState([]);
  const [bureaus, setBureaus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState(null);
  const [isPublicLoggedIn, setIsPublicLoggedIn] = useState(false);

  const [filters, setFilters] = useState(createInitialFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Card click → ProfileDetailsModal (same modal the bureau dashboard +
  // FindMatches use). Heart click → save/unsave, or sign-in prompt for
  // anonymous viewers.
  const [detailsUserId, setDetailsUserId] = useState(null);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const filtersRef = useRef(filters);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("publicUserToken");
    setIsPublicLoggedIn(!!token);
    // Hydrate which profiles this seeker has already saved so the heart
    // icons render filled. Skipped when no token — anonymous viewers
    // can't have saved anything.
    if (token) {
      apiFetch("/public/auth/saved-profiles")
        .then((res) => {
          const ids = (res?.data || []).map((p) => p._id?.toString()).filter(Boolean);
          setSavedIds(new Set(ids));
        })
        .catch(() => {});
    }
  }, []);

  // Heart click handler. Anonymous → open the sign-in prompt. Signed in
  // → toggle save/unsave with optimistic UI (rollback on failure).
  const toggleSave = async (profileId) => {
    if (!isPublicLoggedIn) {
      setShowSignInPrompt(true);
      return;
    }
    if (savingId) return; // ignore double-clicks while a write is in-flight
    setSavingId(profileId);
    const isCurrentlySaved = savedIds.has(profileId);
    // Optimistic update
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlySaved) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
    try {
      await apiFetch(
        isCurrentlySaved ? "/public/auth/remove-saved" : "/public/auth/save-profile",
        {
          method: "POST",
          body: JSON.stringify({ profileId }),
        }
      );
    } catch (err) {
      // Roll back optimistic change on failure
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (isCurrentlySaved) next.add(profileId);
        else next.delete(profileId);
        return next;
      });
      setBanner({
        type: "error",
        text: err.message || "Couldn't update saved profiles",
      });
    } finally {
      setSavingId(null);
    }
  };

  const fetchUsers = useCallback(async (queryParams) => {
    const params = queryParams ?? filtersRef.current;
    try {
      setLoading(true);
      const qs = buildQueryString(params);
      const endpoint = qs ? `/public/listings?${qs}` : "/public/listings";
      const data = await apiFetch(endpoint);
      setUsers(data.data || []);
      setError("");
    } catch (err) {
      console.error("Failed to fetch public listings:", err);
      setError(err.message || "Unable to load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBureaus = useCallback(async () => {
    try {
      const data = await apiFetch("/public/bureaus");
      const bureausArray = data.data || data || [];
      setBureaus(bureausArray);
    } catch (err) {
      console.error("Failed to fetch bureaus:", err);
      setBureaus([]);
    }
  }, []);

  useEffect(() => {
    fetchUsers(filtersRef.current);
    fetchBureaus();
  }, [fetchUsers, fetchBureaus]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const scheduleFetch = (nextFilters) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(nextFilters), 300);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      filtersRef.current = next;
      if (["search", "city"].includes(name)) scheduleFetch(next);
      return next;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchUsers(filtersRef.current);
  };

  const handleResetFilters = () => {
    const reset = createInitialFilters();
    filtersRef.current = reset;
    setFilters(reset);
    setShowAdvancedFilters(false);
    fetchUsers(reset);
  };

  const bannerClasses =
    banner?.type === "success"
      ? "border-[var(--color-notice-border)] bg-[var(--color-notice-bg)] text-[var(--color-notice-text)]"
      : "border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] text-[var(--color-danger-text)]";

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-40 border-b border-[var(--color-border)] bg-[var(--color-surface)] shadow-md">
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          {/* Left Title (replaces the dead hamburger — public-profiles
              has no off-canvas nav to open, so the button was a no-op). */}
          <div className="text-lg font-bold text-[var(--color-text-strong)]">
            Phuphoo
          </div>

          {/* Right Action - Login/Logout */}
          {isPublicLoggedIn ? (
            <button
              onClick={() => {
                localStorage.removeItem("publicUserToken");
                setIsPublicLoggedIn(false);
                navigate("/");
              }}
              className="rounded-lg border border-red-500 px-3 py-2 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
            >
              Logout
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm font-semibold text-[var(--color-on-primary)] shadow-sm transition hover:bg-[var(--color-primary-hover)]"
            >
              Login
            </button>
          )}
        </div>

      </nav>

      <div className="relative p-6 space-y-6">
        {banner && (
          <div
            className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-sm shadow-sm ${bannerClasses}`}
          >
            <span>{banner.text}</span>
            <button
              className="text-xs font-semibold uppercase tracking-wide"
              onClick={() => setBanner(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--color-border)] px-6 py-4">
            <div>
              <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
                Marriage Profiles
              </h2>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                Browse active profiles from every bureau on the platform. Tap any card for full details.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchUsers()}
                disabled={loading}
                className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm font-semibold text-[var(--color-text-strong)] transition hover:bg-[var(--color-surface-hover)] disabled:opacity-60"
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* Filters */}
          <form
            onSubmit={handleFilterSubmit}
            className="space-y-4 border-b border-[var(--color-border)] px-6 py-4"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Search
                </label>
                <input
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search by name, reference ID, contact, city, profession..."
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Bureau
                </label>
                <select
                  name="bureauId"
                  value={filters.bureauId}
                  onChange={handleFilterChange}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                >
                  <option value="">-- All Bureaus --</option>
                  {bureaus && bureaus.length > 0 ? (
                    bureaus.map((bureau) => (
                      <option key={bureau._id} value={bureau._id}>
                        {bureau.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading bureaus...</option>
                  )}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowAdvancedFilters((s) => !s)}
                className="text-sm font-semibold text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] underline-offset-4 hover:underline transition-colors"
              >
                {showAdvancedFilters
                  ? "Hide advanced filters"
                  : "Show advanced filters"}
              </button>

              <div className="ml-auto flex gap-3">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold hover:bg-[var(--color-surface-hover)]"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-on-primary)]"
                >
                  Apply Filters
                </button>
              </div>
            </div>

            {showAdvancedFilters && (
              <div className="grid gap-4 md:grid-cols-3 mt-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={filters.gender}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Marital Status
                  </label>
                  <select
                    name="maritalStatus"
                    value={filters.maritalStatus}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="single">Single</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>
                    <option value="seperated">Seperated</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    City
                  </label>
                  <input
                    name="city"
                    value={filters.city}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="e.g. Lahore"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Age Min
                  </label>
                  <input
                    type="number"
                    min="18"
                    name="minAge"
                    value={filters.minAge}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Age Max
                  </label>
                  <input
                    type="number"
                    min="18"
                    name="maxAge"
                    value={filters.maxAge}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Profession
                  </label>
                  <input
                    name="profession"
                    value={filters.profession}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="e.g. Teacher"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Education
                  </label>
                  <input
                    name="education"
                    value={filters.education}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="e.g. Bachelors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Cast / Caste
                  </label>
                  <input
                    name="cast"
                    value={filters.cast}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="e.g. Arain"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    House Status
                  </label>
                  <select
                    name="houseStatus"
                    value={filters.houseStatus}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="owned">Owned</option>
                    <option value="rented">Rented</option>
                    <option value="parental">Parental</option>
                  </select>
                </div>
              </div>
            )}
          </form>

          {/* Content */}
          <div className="px-6 py-4">
            {error && (
              <div className="text-center text-[var(--color-danger-text)] py-10">
                {error}
              </div>
            )}

            {loading && (
              <div className="text-center text-[var(--color-text-muted)] py-10">
                Loading profiles...
              </div>
            )}

            {!loading && !error && users.length === 0 && (
              <div className="text-center text-[var(--color-text-muted)] py-10">
                No profiles found matching your criteria.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {users.map((u) => {
                const id = u._id?.toString();
                const isSaved = savedIds.has(id);
                return (
                    <div
                      key={u._id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailsUserId(u._id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDetailsUserId(u._id);
                        }
                      }}
                      className="relative overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 hover:shadow-md hover:border-[var(--color-primary)] cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                    >
                      {/* Favorite heart — top-right corner. Filled when saved.
                          stopPropagation so clicking it doesn't open details. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSave(id);
                        }}
                        aria-label={isSaved ? "Remove from saved" : "Save profile"}
                        title={
                          !isPublicLoggedIn
                            ? "Sign in to save profiles"
                            : isSaved
                            ? "Remove from saved"
                            : "Save profile"
                        }
                        className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center shadow-sm hover:bg-[var(--color-surface-hover)] transition-colors"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className={`w-4 h-4 ${
                            isSaved
                              ? "text-red-500 fill-red-500"
                              : "text-[var(--color-text-muted)] fill-none"
                          }`}
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </button>

                      <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                        ID {u.referenceId || "—"}
                      </div>
                      <div className="flex items-center gap-2 mb-3 pr-10">
                        {u.fullName && (
                          <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                            {u.fullName}
                          </h3>
                        )}
                        <GenderBadge gender={u.gender} />
                      </div>

                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">Age</span>
                          <span className="text-[var(--color-text)] font-medium">
                            {u.age ?? "—"}
                          </span>
                        </div>
                        {(u.heightFeet || u.heightInches) && (
                          <div className="flex justify-between gap-2">
                            <span className="text-[var(--color-text-muted)]">Height</span>
                            <span className="text-[var(--color-text)] font-medium">
                              {u.heightFeet ?? "—"}ft {u.heightInches ?? 0}in
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">City</span>
                          <span className="text-[var(--color-text)] font-medium truncate">
                            {u.city || "—"}
                          </span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">Profession</span>
                          <span className="text-[var(--color-text)] font-medium truncate">
                            {u.profession || "—"}
                          </span>
                        </div>
                        {(u.cast || u.caste) && (
                          <div className="flex justify-between gap-2">
                            <span className="text-[var(--color-text-muted)]">Caste</span>
                            <span className="text-[var(--color-text)] font-medium truncate">
                              {u.cast || u.caste}
                            </span>
                          </div>
                        )}
                        {u.maritalStatus && (
                          <div className="flex justify-between gap-2">
                            <span className="text-[var(--color-text-muted)]">Marital</span>
                            <span className="text-[var(--color-text)] font-medium capitalize">
                              {u.maritalStatus}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between gap-2 text-[10px] text-[var(--color-text-muted)]">
                        <span className="truncate">
                          {u.bureau?.name && <>Listed by {u.bureau.name}</>}
                          {u.bureau?.name && u.createdAt && <> · </>}
                          {u.createdAt && formatDate(u.createdAt)}
                        </span>
                        {/* Eye icon + view count — only renders if we
                            actually have data to show. */}
                        <span className="inline-flex items-center gap-1 shrink-0">
                          <svg
                            viewBox="0 0 24 24"
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          {u.views ?? 0}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
          </div>
        </div>
      </div>

      {/* Card click → full profile details (shared modal with FindMatches
          and the bureau Dashboard). */}
      <ProfileDetailsModal
        isOpen={!!detailsUserId}
        userId={detailsUserId}
        onClose={() => setDetailsUserId(null)}
      />

      {/* Anonymous viewer hit the heart — prompt them to sign in. */}
      {showSignInPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-[var(--color-surface)] p-6 shadow-xl">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-lg font-semibold text-[var(--color-text-strong)]">
                Please sign in
              </h3>
              <button
                onClick={() => setShowSignInPrompt(false)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] text-xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-[var(--color-text-muted)] mb-5">
              Sign in to save profiles to your favorites and revisit them
              from your dashboard.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setShowSignInPrompt(false);
                  navigate("/login");
                }}
                className="w-full rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold transition-colors"
              >
                Sign in
              </button>
              <button
                onClick={() => {
                  setShowSignInPrompt(false);
                  navigate("/register");
                }}
                className="w-full rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
              >
                Create an account
              </button>
              <button
                onClick={() => setShowSignInPrompt(false)}
                className="w-full text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] py-1"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
